import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowUp, ArrowDown, Copy, Trash2, Lock, Share2, SlidersHorizontal, ChevronDown, ChevronUp, Info, RotateCcw, Undo2, AlertCircle, Upload, FolderOpen, RefreshCw } from "lucide-react";
import { useStudio, useLookups } from "@/store/StudioContext";
import { useAuth } from "@/store/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  addSceneToTrash,
  removeSceneFromTrash,
  getTrashScenes,
  fetchServerTrash,
  permanentlyDeleteFromTrash,
  recordPermanentlyDeletedScenes,
} from "@/domain/sceneTrash";
import { EmptyState, StatusPill, DemoBadge, Notice } from "@/components/common/States";
import MediaFrame from "@/components/common/MediaFrame";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { sceneAssetIds } from "@/domain/assetUsage";
import { getReadableAssetTitle, isTechnicalName, getOriginalFileTooltip } from "@/domain/assetDisplay";
import DeleteAssetDialog from "@/components/common/DeleteAssetDialog";
import ContinuityIndicator from "@/components/project/ContinuityIndicator";
import ContinuityInspector from "@/components/project/ContinuityInspector";
import { analyzeProjectContinuity } from "@/domain/continuity";
import { getProjectCharacters, getProjectReferences, getAuthorizedProjectAssets, getAllUserGalleryAssets } from "@/domain/librarySelectors";
import { uploadAssetWithProgress } from "@/lib/api";
import { Asset } from "@/domain/entities";
import AssetPicker from "@/components/common/AssetPicker";
import { PromptMentionEditor } from "@/components/common/PromptMentionEditor";
import CharacterViewSelectorModal from "@/components/character/CharacterViewSelectorModal";
import { Reference } from "@/domain/entities";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { resolveSceneDuration, extractProposalsFromMessages } from "@/domain/productionPlan";
import { t } from "@/i18n";

const NEXT_STATUS = { draft: "review", review: "approved", approved: "draft" };

const SceneCard = ({ scene, index, total, selected, cover, onSelect, onMove, onDuplicate, onDelete, onAssignMedia, onUploadMedia, assetById, project, observations, decisions }) => {
  const fallback = scene.assetIds?.find((id) => {
    const a = assetById[id];
    return a && (a.projectId === project.id || scene.assetIds?.includes(a.id));
  });
  const activeMedia = cover || (fallback ? assetById[fallback] : null);

  return (
    <div
      data-testid={`scene-card-${scene.id}`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onSelect()}
      className={`group v-panel v-fade-up cursor-pointer overflow-hidden transition-[border-color,transform] ${selected ? "border-[#f5b544]" : "hover:border-[#4a4a4a]"}`}
      style={{ animationDelay: `${index * 40}ms` }}
    >
      <MediaFrame
        asset={activeMedia}
        fit="contain"
        className="aspect-video rounded-none border-0 bg-[#121212]"
      />
      {!activeMedia && (
        <div className="px-3 pt-2 flex flex-col gap-1.5">
          <button
            type="button"
            data-testid={`scene-empty-assign-${scene.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
              if (onAssignMedia) onAssignMedia(scene);
            }}
            className="flex w-full items-center justify-center gap-1.5 rounded border border-[#f5b544]/60 bg-[#f5b544]/10 py-1.5 px-2 text-xs font-medium text-[#f5b544] hover:bg-[#f5b544]/20 transition-colors"
            title={t.storyboard.chooseMediaFromLibrary || "Elegir medio de la biblioteca"}
          >
            <FolderOpen size={13} />
            <span>{t.storyboard.chooseMediaFromLibrary || "Elegir medio de la biblioteca"}</span>
          </button>
          <button
            type="button"
            data-testid={`scene-empty-upload-${scene.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
              if (onUploadMedia) onUploadMedia(scene);
            }}
            className="flex w-full items-center justify-center gap-1.5 rounded border border-[#333] bg-[#1a1a1a] py-1 px-2 text-[11px] text-[#a3a3a3] hover:text-[#f4f1ec] hover:border-[#444] transition-colors"
            title={t.storyboard.uploadFromDevice || "Subir desde mi dispositivo"}
          >
            <Upload size={12} />
            <span>{t.storyboard.uploadFromDevice || "Subir desde mi dispositivo"}</span>
          </button>
        </div>
      )}
      <div className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[11px] text-[#a3a3a3]">{t.storyboard.sceneN(index + 1)}</span>
            {scene.duration != null && (
              <span
                data-testid={`scene-card-duration-${scene.id}`}
                className="font-mono text-[10px] text-[#f5b544] bg-[#f5b544]/10 rounded px-1.5 py-0.2 border border-[#f5b544]/20"
              >
                {scene.duration}s
              </span>
            )}
            {Array.isArray(scene.missingReferences) && scene.missingReferences.length > 0 && (
              <span
                data-testid={`scene-card-missing-badge-${scene.id}`}
                className="font-mono text-[10px] text-amber-300 bg-amber-950/40 rounded px-1.5 py-0.2 border border-amber-500/30"
                title={`${scene.missingReferences.length} referencias pendientes`}
              >
                âš ï¸ {scene.missingReferences.length} pend.
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <ContinuityIndicator sceneId={scene.id} observations={observations} decisions={decisions} />
            <StatusPill status={scene.status} map={t.storyboard.status} />
          </div>
        </div>
        <div className="mt-1 truncate text-sm font-medium">{scene.title || t.common.untitled}</div>
        <p className="mt-1 line-clamp-2 min-h-[2.5em] text-xs leading-relaxed text-[#a3a3a3]">{scene.prompt || "â€”"}</p>
        <div className="mt-2 flex items-center gap-0.5 opacity-70 transition-opacity group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
          <button data-testid={`scene-move-up-${scene.id}`} disabled={index === 0} onClick={() => onMove(-1)} className="rounded p-1 text-[#a3a3a3] hover:bg-[#222] hover:text-[#f4f1ec] disabled:opacity-30" aria-label={t.storyboard.moveUp}><ArrowUp size={14} /></button>
          <button data-testid={`scene-move-down-${scene.id}`} disabled={index === total - 1} onClick={() => onMove(1)} className="rounded p-1 text-[#a3a3a3] hover:bg-[#222] hover:text-[#f4f1ec] disabled:opacity-30" aria-label={t.storyboard.moveDown}><ArrowDown size={14} /></button>
          <button data-testid={`scene-duplicate-${scene.id}`} onClick={onDuplicate} className="rounded p-1 text-[#a3a3a3] hover:bg-[#222] hover:text-[#f4f1ec]" aria-label={t.common.duplicate}><Copy size={14} /></button>
          <button data-testid={`scene-delete-${scene.id}`} onClick={onDelete} className="ml-auto rounded p-1 text-[#6b6b6b] hover:bg-[#222] hover:text-[#e5655a]" aria-label={t.common.delete}><Trash2 size={14} /></button>
          {scene.isDemo && <DemoBadge className="ml-1" />}
        </div>
      </div>
    </div>
  );
};

const SceneDetail = ({
  scene,
  project = null,
  projectId,
  scenes = [],
  observations = [],
  decisions = {},
  characters = [],
  references = null,
  onAssignMedia,
  onUploadMedia,
}) => {
  const { state, dispatch } = useStudio();
  const { assetById, referenceById, projectById } = useLookups();
  const navigate = useNavigate();
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [originExpanded, setOriginExpanded] = useState(false);
  const [activeCharacterModal, setActiveCharacterModal] = useState(null);

  const availableCharacters = useMemo(() => {
    if (Array.isArray(characters) && characters.length > 0) {
      return characters;
    }
    return projectId ? getProjectCharacters(state, projectId) : [];
  }, [characters, state, projectId]);

  const availableReferences = useMemo(() => {
    if (Array.isArray(references) && references.length > 0) {
      return references;
    }
    return projectId ? getProjectReferences(state, projectId) : [];
  }, [references, state, projectId]);

  const sceneCharacterReferences = useMemo(() => {
    const refIds = Array.isArray(scene.referenceIds) ? scene.referenceIds : [];
    const result = [];
    for (const refId of refIds) {
      const ref = (state.references || []).find((r) => r.id === refId);
      if (!ref) continue;
      if (ref.characterId) {
        const char = (availableCharacters || []).find((c) => c.id === ref.characterId);
        result.push({
          referenceId: ref.id,
          characterId: ref.characterId,
          characterHandle: char ? char.handle : (ref.label || "").replace(/^@+/, ""),
          assetId: ref.assetId,
          label: ref.label,
        });
      }
    }
    return result;
  }, [scene.referenceIds, state.references, availableCharacters]);

  const patch = (p) => dispatch({ type: "SCENE_UPDATE", payload: { id: scene.id, patch: p } });

  const handleSelectCharacterMention = (char) => {
    if (char?.id) {
      const currentCharIds = Array.isArray(scene.characterIds) ? scene.characterIds : [];
      if (!currentCharIds.includes(char.id)) {
        patch({ characterIds: [...currentCharIds, char.id] });
      }
    }
    setActiveCharacterModal(char);
  };

  const otherRefsCount = useMemo(() => {
    if (!activeCharacterModal) return 0;
    const currentRefIds = Array.isArray(scene.referenceIds) ? scene.referenceIds : [];
    return currentRefIds.filter((refId) => {
      const r = (state.references || []).find((ref) => ref.id === refId);
      return r && r.characterId !== activeCharacterModal.id;
    }).length;
  }, [scene.referenceIds, state.references, activeCharacterModal]);

  const maxAllowedForModal = Math.max(1, 2 - otherRefsCount);

  const handleConfirmCharacterViews = (chosenViews) => {
    if (!activeCharacterModal) return;
    const charId = activeCharacterModal.id;
    const currentRefIds = Array.isArray(scene.referenceIds) ? scene.referenceIds : [];

    // Referencias de otros personajes o recursos que no corresponden a este personaje
    const otherRefIds = currentRefIds.filter((refId) => {
      const r = (state.references || []).find((ref) => ref.id === refId);
      return r && r.characterId !== charId;
    });

    // Cuota mÃ¡xima disponible respetando el lÃ­mite total estricto de 2 referencias
    const quota = Math.max(0, 2 - otherRefIds.length);
    const cappedChosen = (chosenViews || []).slice(0, quota);

    const newCharRefIds = [];
    for (const v of cappedChosen) {
      const existingRef = (state.references || []).find(
        (r) =>
          r.characterId === charId &&
          r.assetId === v.assetId &&
          (r.projectId === projectId || r.scope === "global")
      );
      if (!existingRef) {
        const clean = (activeCharacterModal.handle || "").replace(/^@+/, "").trim();
        const created = Reference({
          kind: "character",
          characterId: charId,
          assetId: v.assetId,
          label: `@${clean} â€” ${v.viewLabel || "Vista"}`,
          projectId,
          scope: "project",
        });
        dispatch({ type: "REFERENCE_CREATE", payload: created });
        newCharRefIds.push(created.id);
      } else {
        newCharRefIds.push(existingRef.id);
      }
    }

    const newRefIds = [...otherRefIds, ...newCharRefIds].slice(0, 2);
    const currentCharIds = Array.isArray(scene.characterIds) ? scene.characterIds : [];
    const newCharIds = currentCharIds.includes(charId) ? currentCharIds : [...currentCharIds, charId];

    patch({
      referenceIds: newRefIds,
      characterIds: newCharIds,
    });

    setActiveCharacterModal(null);
  };

  const selectedAsset = scene.selectedAssetId ? assetById[scene.selectedAssetId] : null;
  const originJob = useMemo(() => {
    if (!selectedAsset) return null;
    return (
      (state.jobs || []).find(
        (j) =>
          j.resultAssetId === selectedAsset.id ||
          j.resultAssetIds?.includes(selectedAsset.id) ||
          (selectedAsset.jobId && (j.id === selectedAsset.jobId || j.backendJobId === selectedAsset.jobId))
      ) || null
    );
  }, [selectedAsset, state.jobs]);

  const currentPromptText = (scene.prompt || "").trim();
  const originPromptText = (originJob?.prompt || "").trim();
  const hasPromptMismatch = Boolean(
    selectedAsset &&
    currentPromptText &&
    originPromptText &&
    currentPromptText !== originPromptText
  );

  const hasOriginDetails = Boolean(originPromptText || originJob?.modelId || originJob?.providerId);

  // ResoluciÃ³n de lectura de medios de la escena:
  // scene.assetIds + scene.selectedAssetId + activos con sceneId, sin duplicados y filtrando por projectId
  const sceneAssetList = useMemo(() => {
    const rawIds = sceneAssetIds(scene, state.assets);
    const seen = new Set();
    const result = [];
    for (const id of rawIds) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const a = assetById[id];
      if (a && a.projectId === projectId) {
        result.push(id);
      }
    }
    return result;
  }, [scene, state.assets, assetById, projectId]);

  const NEXT_LABEL = { draft: t.storyboard.toReview, review: t.storyboard.approve, approved: t.storyboard.toDraft };
  const [confirmDurationOpen, setConfirmDurationOpen] = useState(false);

  const currentProject =
    project ||
    projectById?.[projectId] ||
    state.projects?.find((p) => p.id === projectId);
  const activeScript = state.scripts?.find(
    (s) => s.id === currentProject?.activeScriptId || (s.projectId === projectId && s.isActive)
  );

  const threadMessages =
    state.threads?.find((th) => th.projectId === projectId)?.messages || [];
  const allProposals = [
    ...(state.proposals || []),
    ...extractProposalsFromMessages(threadMessages),
  ];
  const recoverableMontage = resolveSceneDuration(scene, {
    proposals: allProposals,
    activeScript,
    projectId,
  });

  return (
    <div data-testid="scene-detail" className="flex flex-col gap-4 p-4 md:p-5 pb-16">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="v-eyebrow">{t.storyboard.detail}</div>
          <ContinuityIndicator sceneId={scene.id} observations={observations} decisions={decisions} />
        </div>
        <button data-testid="scene-status-toggle" className="v-btn-ghost v-btn-sm" onClick={() => patch({ status: NEXT_STATUS[scene.status] })}>{NEXT_LABEL[scene.status]}</button>
      </div>
      <div className="flex items-center gap-2">
        <input
          data-testid="scene-title-input"
          className="v-input font-medium flex-1"
          placeholder={t.storyboard.title}
          value={scene.title || ""}
          onChange={(e) => patch({ title: e.target.value })}
        />
        <div className="flex items-center gap-1.5 shrink-0 rounded-md border border-[#303030] bg-[#141414] px-2.5 py-1.5">
          <span className="text-xs text-[#888]">DuraciÃ³n:</span>
          <input
            data-testid="scene-duration-input"
            type="number"
            min="1"
            max="60"
            className="w-12 bg-transparent text-xs text-[#f5b544] font-mono focus:outline-none"
            placeholder="s"
            value={scene.duration ?? ""}
            onChange={(e) => {
              const val = e.target.value === "" ? null : Number(e.target.value);
              patch({ duration: val });
            }}
          />
          <span className="text-xs text-[#888]">seg</span>
        </div>
      </div>

      {recoverableMontage.isRecovered && !recoverableMontage.isSaved && (
        <div
          data-testid="storyboard-recoverable-duration-box"
          className="flex flex-col gap-1.5 p-2.5 rounded-lg bg-[#181818] border border-[#2a2a2a] text-xs"
        >
          <span className="font-mono text-[11px] text-[#f5b544]">
            {recoverableMontage.duration} s previstos â€” pendientes de consolidar en la escena
          </span>
          <button
            type="button"
            data-testid="storyboard-confirm-duration-btn"
            onClick={() => setConfirmDurationOpen(true)}
            className="inline-flex items-center gap-1 rounded bg-[#f5b544]/15 border border-[#f5b544]/40 px-2 py-0.5 text-[11px] font-semibold text-[#f5b544] hover:bg-[#f5b544]/25 transition-colors cursor-pointer w-fit"
          >
            Confirmar duraciÃ³n de {recoverableMontage.duration} s
          </button>
        </div>
      )}

      {/* DiÃ¡logo de confirmaciÃ³n de duraciÃ³n en Storyboard */}
      <Dialog open={confirmDurationOpen} onOpenChange={setConfirmDurationOpen}>
        <DialogContent
          data-testid="storyboard-confirm-duration-dialog"
          className="max-w-md border border-[#303030] bg-[#181818] p-5 text-slate-100 shadow-2xl"
        >
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-[#f4f1ec]">
              Confirmar duraciÃ³n de montaje ({recoverableMontage.duration} s)
            </DialogTitle>
            <DialogDescription className="text-xs text-[#a3a3a3] mt-2 space-y-2 leading-relaxed">
              <span>
                Esta acciÃ³n establecerÃ¡ la duraciÃ³n de montaje de la toma{" "}
                <strong className="text-[#f4f1ec]">
                  Â«{scene.title || (() => {
                    const idx = scenes.findIndex((s) => s.id === scene.id);
                    return idx >= 0 ? `Toma ${idx + 1}` : "esta toma";
                  })()}Â»
                </strong>{" "}
                en <strong className="text-[#f5b544]">{recoverableMontage.duration} s</strong> en el Storyboard del proyecto activo, sin cambiar la duraciÃ³n generada del nodo de vÃ­deo.
              </span>
              <span className="block text-[11px] text-[#777] font-mono mt-1">
                Procedencia: {recoverableMontage.details || recoverableMontage.source}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 flex items-center justify-end gap-2 border-t border-[#262626] pt-3">
            <button
              type="button"
              data-testid="storyboard-cancel-duration-btn"
              onClick={() => setConfirmDurationOpen(false)}
              className="v-btn-ghost v-btn-sm text-xs"
            >
              Cancelar
            </button>
            <button
              type="button"
              data-testid="storyboard-save-duration-confirm-btn"
              onClick={() => {
                const freshScenes = state.scenes || [];
                const freshScene = freshScenes.find((s) => s.id === scene?.id);
                if (!freshScene || freshScene.projectId !== projectId) {
                  setConfirmDurationOpen(false);
                  return;
                }
                if (typeof freshScene.duration === "number" && freshScene.duration > 0) {
                  setConfirmDurationOpen(false);
                  return;
                }
                const rechecked = resolveSceneDuration(freshScene, {
                  proposals: allProposals,
                  activeScript,
                  projectId,
                });
                if (!rechecked.isRecovered || typeof rechecked.duration !== "number") {
                  setConfirmDurationOpen(false);
                  return;
                }
                patch({ duration: rechecked.duration });
                setConfirmDurationOpen(false);
              }}
              className="v-btn-primary v-btn-sm text-xs font-semibold"
            >
              Confirmar duraciÃ³n de {recoverableMontage.duration} s
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {scene.scriptId && (
        <div data-testid="scene-script-link" className="flex items-center justify-between rounded-md border border-[#2a2a2a] bg-[#141414] px-3 py-2 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-[#6b6b6b]">Guion vinculado:</span>
            <span className="font-medium text-[#d8d4cd]">
              {state.scripts?.find((s) => s.id === scene.scriptId)?.title || scene.scriptId}
            </span>
          </div>
          {scene.scriptVersionId && (
            <span className="rounded bg-[#222] px-2 py-0.5 font-mono text-[11px] text-[#f5b544] border border-[#333]">
              VersiÃ³n {scene.scriptVersionId}
            </span>
          )}
        </div>
      )}
      <div>
        <div className="mb-1 text-xs font-medium text-[#a3a3a3]">
          {t.storyboard.currentPrompt || "Prompt de la toma"}
        </div>
        <p className="mb-1.5 text-[11px] leading-relaxed text-[#737373]">
          {t.storyboard.promptHelp || "Describe quÃ© debe aparecer y suceder en esta toma. Puedes escribirlo o prepararlo con el Creative Director."}
        </p>
        <PromptMentionEditor
          testId="scene-prompt-input"
          className=""
          rows={5}
          placeholder={t.storyboard.promptPh}
          value={scene.prompt || ""}
          onChange={(text) => patch({ prompt: text })}
          characters={availableCharacters}
          characterReferences={sceneCharacterReferences}
          assetById={assetById}
          onSelectCharacterMention={handleSelectCharacterMention}
          supportsFrames={false}
        />
        {hasPromptMismatch && (
          <div
            data-testid="scene-prompt-mismatch-notice"
            className="mt-2 flex items-start gap-2 rounded-md border border-[#383838] bg-[#1a1a1a] p-2.5 text-xs text-[#d8d4cd]"
          >
            <Info size={14} className="mt-0.5 shrink-0 text-[#f5b544]" />
            <span>{t.storyboard.promptMismatchNotice}</span>
          </div>
        )}
        <div className="mt-2 rounded-md border border-[#2a2a2a] bg-[#141414]/60">
          <button
            type="button"
            data-testid="scene-media-origin-toggle"
            onClick={() => setOriginExpanded((prev) => !prev)}
            className="flex w-full items-center justify-between px-3 py-2 text-left text-xs text-[#a3a3a3] hover:text-[#f4f1ec]"
          >
            <span className="font-medium">{t.storyboard.selectedMediaOrigin}</span>
            {originExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {originExpanded && (
            <div data-testid="scene-media-origin-content" className="border-t border-[#262626] p-3 text-xs">
              {hasOriginDetails ? (
                <div className="space-y-2">
                  {(originJob?.modelId || originJob?.providerId) && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#a3a3a3]">
                      {originJob.modelId && (
                        <span>
                          <span className="text-[#6b6b6b]">{t.storyboard.originModel}:</span>{" "}
                          <span className="font-mono text-[#d8d4cd]">{originJob.modelId}</span>
                        </span>
                      )}
                      {originJob.providerId && (
                        <span>
                          <span className="text-[#6b6b6b]">{t.storyboard.originProvider}:</span>{" "}
                          <span className="font-mono text-[#d8d4cd]">{originJob.providerId}</span>
                        </span>
                      )}
                    </div>
                  )}
                  {originPromptText ? (
                    <div>
                      <div className="mb-1 text-[11px] text-[#6b6b6b]">{t.storyboard.originPrompt}:</div>
                      <div
                        data-testid="scene-origin-prompt-text"
                        className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded border border-[#2a2a2a] bg-[#0d0d0d] p-2 font-mono text-[11px] leading-relaxed text-[#c5c1ba] select-text"
                      >
                        {originPromptText}
                      </div>
                    </div>
                  ) : (
                    <p data-testid="scene-origin-prompt-empty" className="text-[11px] text-[#737373] italic">
                      {t.storyboard.originPromptNotAvailable}
                    </p>
                  )}
                </div>
              ) : (
                <p data-testid="scene-origin-prompt-empty" className="text-[11px] text-[#737373] italic">
                  {t.storyboard.originPromptNotAvailable}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      <div>
        <div className="mb-1 text-xs font-medium text-[#a3a3a3]">AcciÃ³n</div>
        <p className="mb-1.5 text-[11px] leading-relaxed text-[#737373]">
          DescripciÃ³n narrativa del movimiento, actuaciÃ³n o sucesos de la toma.
        </p>
        <textarea
          data-testid="scene-action-input"
          className="v-textarea"
          rows={2}
          placeholder="AcciÃ³n narrativa de la toma..."
          value={scene.action || ""}
          onChange={(e) => patch({ action: e.target.value })}
        />
      </div>
      <div>
        <div className="mb-1 text-xs font-medium text-[#a3a3a3]">CÃ¡mara</div>
        <p className="mb-1.5 text-[11px] leading-relaxed text-[#737373]">
          Encuadre, distancia focal, altura y movimiento de cÃ¡mara.
        </p>
        <input
          data-testid="scene-camera-input"
          className="v-input"
          placeholder="Ej: Plano medio corto (MCU) â€” EstÃ¡tico a la altura de los ojos"
          value={scene.camera || ""}
          onChange={(e) => patch({ camera: e.target.value })}
        />
      </div>
      <div>
        <div className="mb-1 text-xs font-medium text-[#a3a3a3]">Sonidos previstos</div>
        <p className="mb-1.5 text-[11px] leading-relaxed text-[#737373]">
          Efectos sonoros, foley, mÃºsica diegÃ©tica o ambientaciÃ³n sonora.
        </p>
        <input
          data-testid="scene-sound-input"
          className="v-input"
          placeholder="Ej: Click nÃ­tido de botÃ³n, zumbido estÃ¡tico..."
          value={scene.planned_sound || ""}
          onChange={(e) => patch({ planned_sound: e.target.value })}
        />
      </div>
      {Array.isArray(scene.missingReferences) && scene.missingReferences.length > 0 && (
        <div data-testid="scene-missing-references" className="rounded-md border border-amber-900/40 bg-amber-950/15 p-3">
          <div className="flex items-center gap-1.5 text-xs font-medium text-amber-400 mb-1">
            <Info size={14} className="shrink-0" />
            <span>Referencias pendientes de la propuesta ({scene.missingReferences.length})</span>
          </div>
          <p className="text-[11px] text-[#a3a3a3] mb-2 leading-relaxed">
            Elementos narrativos del guion pendientes de creaciÃ³n o asignaciÃ³n visual. No son referencias asignadas a la generaciÃ³n:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {scene.missingReferences.map((item, idx) => {
              const label = typeof item === "string" ? item : (item.handle ? `@${item.handle}` : (item.name || item.raw || `Ref ${idx + 1}`));
              return (
                <span
                  key={idx}
                  data-testid={`scene-missing-ref-item-${idx}`}
                  className="inline-flex items-center gap-1.5 rounded bg-[#1e170a] border border-amber-500/30 px-2 py-0.5 text-xs font-mono text-amber-200"
                >
                  <span className="text-amber-400">âš ï¸</span>
                  <span>{label}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}
      <div>
        <div className="mb-1 text-xs font-medium text-[#a3a3a3]">{t.storyboard.notes}</div>
        <p className="mb-1.5 text-[11px] leading-relaxed text-[#737373]">
          {t.storyboard.notesHelp || "Anota detalles que deben mantenerse entre tomas: vestuario, objetos, posiciÃ³n, iluminaciÃ³n o estado de la acciÃ³n."}
        </p>
        <textarea data-testid="scene-notes-input" className="v-textarea" rows={2} placeholder={t.storyboard.notesPh} value={scene.notes} onChange={(e) => patch({ notes: e.target.value })} />
      </div>
      <div>
        <div className="mb-1.5 text-xs font-medium text-[#a3a3a3]">{t.common.references}</div>
        <div className="flex flex-wrap gap-1.5">
          {availableReferences.map((r) => {
            const on = (scene.referenceIds || []).includes(r.id);
            const canToggle = on || (scene.referenceIds || []).length < 2;
            return (
              <button
                key={r.id}
                data-testid={`scene-ref-toggle-${r.id}`}
                disabled={!canToggle}
                title={!canToggle ? "MÃ¡ximo 2 imÃ¡genes de referencia permitidas" : undefined}
                onClick={() => {
                  if (canToggle) {
                    dispatch({
                      type: "SCENE_TOGGLE_REFERENCE",
                      payload: { sceneId: scene.id, referenceId: r.id },
                    });
                  }
                }}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono text-xs transition-colors ${on
                    ? "border-[#f5b544] bg-[#f5b544]/10 text-[#f5b544]"
                    : canToggle
                      ? "border-[#303030] text-[#a3a3a3] hover:text-[#f4f1ec]"
                      : "border-[#222] text-[#555] opacity-50 cursor-not-allowed"
                  }`}
              >
                <MediaFrame asset={assetById[r.assetId]} className="h-5 w-5 rounded-sm border-0" showBadge={false} />
                {r.label}
              </button>
            );
          })}
          {availableReferences.length === 0 && <span className="text-xs text-[#a3a3a3]">{t.storyboard.noReferences}</span>}
        </div>
      </div>
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <div className="text-xs font-medium text-[#a3a3a3]">{t.storyboard.sceneMedia}</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="scene-pick-library-btn"
              onClick={() => onAssignMedia && onAssignMedia(scene)}
              className="flex items-center gap-1 text-xs text-[#f5b544] hover:underline"
              title={t.storyboard.chooseFromGallery || "Elegir de la galerÃ­a"}
            >
              <FolderOpen size={12} />
              <span>{t.storyboard.chooseFromGallery || "Elegir de la galerÃ­a"}</span>
            </button>
            <button
              type="button"
              data-testid="scene-upload-device-btn"
              onClick={() => onUploadMedia && onUploadMedia(scene)}
              className="flex items-center gap-1 text-xs text-[#a3a3a3] hover:text-[#f4f1ec] hover:underline"
              title={t.storyboard.uploadFromDevice || "Subir desde mi dispositivo"}
            >
              <Upload size={12} />
              <span>{t.storyboard.uploadFromDevice || "Subir desde mi dispositivo"}</span>
            </button>
          </div>
        </div>
        {sceneAssetList.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded border border-dashed border-[#333] p-4 text-center">
            <Notice testId="scene-no-assets">{t.storyboard.noSceneMedia}</Notice>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                data-testid="scene-empty-pick-library-btn"
                onClick={() => onAssignMedia && onAssignMedia(scene)}
                className="v-btn-primary v-btn-sm"
              >
                <FolderOpen size={13} />
                <span>{t.storyboard.chooseFromGallery || "Elegir de la galerÃ­a"}</span>
              </button>
              <button
                type="button"
                data-testid="scene-empty-upload-device-btn"
                onClick={() => onUploadMedia && onUploadMedia(scene)}
                className="v-btn-ghost v-btn-sm border border-[#333] text-[#d8d4cd] hover:text-[#f4f1ec]"
              >
                <Upload size={13} />
                <span>{t.storyboard.uploadFromDevice || "Subir desde mi dispositivo"}</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2" data-testid="scene-media-grid">
            {sceneAssetList.map((id) => {
              const asset = assetById[id];
              const isSelected = scene.selectedAssetId === id;
              const displayTitle = getReadableAssetTitle({ asset, scene });
              const isTechnical = isTechnicalName(asset?.label);
              const originalTooltip = getOriginalFileTooltip({ asset });
              const isVideo = asset?.type === "video";
              return (
                <div key={id} className="group relative">
                  <button
                    data-testid={`scene-asset-${id}`}
                    title={isTechnical && originalTooltip ? `${displayTitle} (${originalTooltip})` : displayTitle}
                    onClick={() => dispatch({ type: "SCENE_SELECT_ASSET", payload: { sceneId: scene.id, assetId: id } })}
                    className={`block w-full rounded border p-1 text-left transition-colors ${isSelected ? "border-[#f5b544] bg-[#f5b544]/5" : "border-[#2a2a2a] hover:border-[#3d3d3d]"
                      }`}
                  >
                    <div className="relative aspect-video overflow-hidden rounded bg-[#121212]">
                      <MediaFrame asset={asset} fit="contain" className="h-full w-full bg-[#121212]" showBadge={false} />
                      <div className="absolute bottom-1 left-1 rounded bg-black/80 px-1 py-0.5 text-[9px] font-medium text-[#d8d4cd]">
                        {isVideo ? "â–¶ VÃ­deo" : "Imagen"}
                      </div>
                      {isSelected && (
                        <div
                          data-testid={`scene-asset-selected-badge-${id}`}
                          className="absolute right-1 top-1 rounded bg-[#f5b544] px-1 py-0.5 text-[9px] font-semibold text-black"
                        >
                          {t.storyboard.selectedMediaBadge || "Seleccionado"}
                        </div>
                      )}
                    </div>
                    <div className="mt-1 truncate text-[11px] font-medium text-[#d8d4cd]" title={displayTitle}>
                      {displayTitle}
                    </div>
                    {isTechnical && originalTooltip && (
                      <div className="truncate text-[9px] text-[#737373]" title={originalTooltip}>
                        {originalTooltip}
                      </div>
                    )}
                  </button>
                  <button
                    type="button"
                    data-testid={`scene-asset-delete-${id}`}
                    title="Quitar o eliminar"
                    aria-label="Quitar o eliminar"
                    onClick={() => setAssetToDelete(id)}
                    className="absolute right-2 top-2 rounded bg-[#101010]/85 p-1 text-[#d8d4cd] opacity-70 hover:bg-red-500/30 hover:text-red-300 group-hover:opacity-100"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <DeleteAssetDialog open={Boolean(assetToDelete)} assetIds={assetToDelete ? [assetToDelete] : []} sceneId={scene.id} onClose={() => setAssetToDelete(null)} />
      </div>
      <div className="mt-1 flex flex-wrap gap-2">
        <button
          data-testid="scene-generate"
          className="v-btn-primary v-btn-sm opacity-60 cursor-not-allowed"
          disabled
          title={t.storyboard.directGenerationHint}
        >
          <Lock size={12} />
          {t.storyboard.directGenerationSoon}
        </button>
        <button data-testid="scene-open-canvas" className="v-btn-ghost v-btn-sm" onClick={() => navigate(`/projects/${projectId}/canvas`)}><Share2 size={12} />{t.storyboard.openInCanvas}</button>
        <button data-testid="scene-open-editor" className="v-btn-ghost v-btn-sm" disabled={!scene.selectedAssetId && sceneAssetList.length === 0} onClick={() => { if (!scene.selectedAssetId && sceneAssetList[0]) dispatch({ type: "SCENE_SELECT_ASSET", payload: { sceneId: scene.id, assetId: sceneAssetList[0] } }); navigate(`/projects/${projectId}/editor`); }}><SlidersHorizontal size={12} />{t.storyboard.openInEditor}</button>
      </div>
      <p className="text-[11px] text-[#a3a3a3]" data-testid="scene-generate-hint">
        {t.storyboard.directGenerationHint}
      </p>
      <p className="text-[11px] text-[#a3a3a3]">{t.common.id}: <span className="font-mono">{scene.id}</span> Â· {scene.referenceIds.map((r) => referenceById[r]?.label).filter(Boolean).join(", ") || t.storyboard.noReferences}</p>

      {/* Inspector interactivo de continuidad cinematogrÃ¡fica */}
      <ContinuityInspector
        projectId={projectId}
        selectedScene={scene}
        scenes={scenes}
        observations={observations}
        decisions={decisions}
      />

      {activeCharacterModal && (
        <CharacterViewSelectorModal
          open={Boolean(activeCharacterModal)}
          onClose={() => setActiveCharacterModal(null)}
          character={activeCharacterModal}
          assetById={assetById}
          alreadySelectedAssetIds={sceneCharacterReferences
            .filter((cr) => cr.characterId === activeCharacterModal.id)
            .map((cr) => cr.assetId)}
          onConfirm={handleConfirmCharacterViews}
          maxAllowed={maxAllowedForModal}
          autoSelectMaster={false}
        />
      )}
    </div>
  );
};

const StoryboardView = ({ data }) => {
  const { state, dispatch } = useStudio();
  const { user, isOwner } = useAuth();
  const { assetById } = useLookups();
  const { project, scenes, selection, characters: projectCharacters, references: projectReferences } = data;
  const [toDelete, setToDelete] = useState(null);
  const [detailExpanded, setDetailExpanded] = useState(true);

  const userId = user?._id || user?.id || "guest";
  const [undoNotice, setUndoNotice] = useState(null);
  const [trashOpen, setTrashOpen] = useState(false);
  const [trashScenes, setTrashScenes] = useState(() => getTrashScenes(userId, project.id));
  const [trashSyncError, setTrashSyncError] = useState(false);
  const [isTrashLoading, setIsTrashLoading] = useState(false);
  const [restoringIds, setRestoringIds] = useState(new Set());
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [deleteFilesCheckbox, setDeleteFilesCheckbox] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [purgePreviewLoading, setPurgePreviewLoading] = useState(false);
  const [purgePreviewData, setPurgePreviewData] = useState(null);

  const trashReqIdRef = useRef(0);

  const refreshTrash = useCallback(async () => {
    const reqId = ++trashReqIdRef.current;
    setIsTrashLoading(true);
    try {
      const list = await fetchServerTrash(userId, project.id, api);
      if (reqId === trashReqIdRef.current) {
        setTrashScenes(list);
        setTrashSyncError(false);
        setIsTrashLoading(false);
      }
    } catch {
      if (reqId === trashReqIdRef.current) {
        setTrashScenes(getTrashScenes(userId, project.id));
        setTrashSyncError(true);
        setIsTrashLoading(false);
      }
    }
  }, [userId, project.id]);

  useEffect(() => {
    refreshTrash();
  }, [trashOpen, refreshTrash]);

  const handleDeleteScene = async (scene) => {
    if (!scene) return;
    const nodeIds = state.nodes.filter((n) => n.data?.sceneId === scene.id).map((n) => n.id);
    const nodes = state.nodes.filter((n) => nodeIds.includes(n.id));
    const edges = state.edges.filter((e) => nodeIds.includes(e.sourceId) || nodeIds.includes(e.targetId));

    const payload = {
      project_id: project.id,
      scene,
      original_order: scene.order,
      nodes,
      edges,
    };

    // 1. Guardar primero en el servidor para asegurar la recuperaciÃ³n
    let savedOnServer = false;
    try {
      if (api && typeof api.addSceneToTrash === "function") {
        const res = await api.addSceneToTrash(payload);
        if (res && res.ok) {
          savedOnServer = true;
        }
      }
    } catch (err) {
      console.warn("Fallo al guardar escena en papelera del servidor:", err);
    }

    // 2. Guardar en local de respaldo
    const localItem = addSceneToTrash(userId, project.id, scene, {
      originalOrder: scene.order,
      nodes,
      edges,
    });

    // 3. Regla: No retires la escena activa antes de asegurar su recuperaciÃ³n
    if (!savedOnServer && !localItem) {
      toast.error("No se pudo asegurar la persistencia en papelera. La escena activa no fue retirada.");
      return;
    }

    // 4. Retirar de la escena activa Ãºnicamente tras asegurar su recuperaciÃ³n
    dispatch({ type: "SCENE_DELETE", payload: { id: scene.id } });
    setToDelete(null);
    setUndoNotice({
      id: scene.id,
      scene,
      originalOrder: scene.order,
      nodes,
      edges,
    });
    setTrashScenes((prev) => {
      const exists = prev.some((it) => (it.scene?.id || it.id || it.scene_id) === scene.id);
      if (exists) return prev;
      return [localItem, ...prev];
    });
    refreshTrash();
  };

  const handleUndoDelete = async () => {
    if (!undoNotice) return;
    const sceneId = undoNotice.scene?.id || undoNotice.id;
    if (restoringIds.has(sceneId)) return;
    setRestoringIds((prev) => new Set(prev).add(sceneId));

    try {
      // 1. Restaurar en el servidor de forma persistente
      let res = null;
      try {
        if (api && typeof api.restoreSceneFromTrash === "function") {
          res = await api.restoreSceneFromTrash(sceneId, project.id);
        }
      } catch (err) {
        console.warn("Fallo al restaurar en servidor:", err);
      }

      // Regla: No limpies la copia local hasta confirmar la restauraciÃ³n persistida en servidor
      if (!res || !res.ok) {
        toast.error("No se pudo confirmar la restauraciÃ³n en el servidor. La copia de respaldo local fue preservada.");
        return;
      }

      // 2. Solo tras confirmar la restauraciÃ³n persistida en servidor, remover de la papelera local
      removeSceneFromTrash(userId, project.id, sceneId);
      setTrashScenes((prev) => prev.filter((it) => (it.scene?.id || it.id || it.scene_id) !== sceneId));

      const effective = res?.item || undoNotice;
      const sceneData = effective.scene || effective;
      const originalOrder = effective.original_order ?? effective.originalOrder ?? undoNotice.originalOrder;
      const nodes = effective.nodes || undoNotice.nodes || [];
      const edges = effective.edges || undoNotice.edges || [];

      dispatch({
        type: "SCENE_RESTORE",
        payload: {
          scene: sceneData,
          originalOrder,
          nodes,
          edges,
        },
      });
      setUndoNotice(null);
      refreshTrash();
    } finally {
      setRestoringIds((prev) => {
        const next = new Set(prev);
        next.delete(sceneId);
        return next;
      });
    }
  };

  const handleRestoreFromTrash = async (item) => {
    const sceneId = item.scene?.id || item.id || item.scene_id;
    if (restoringIds.has(sceneId)) return;
    setRestoringIds((prev) => new Set(prev).add(sceneId));

    try {
      // 1. Restaurar en el servidor de forma persistente
      let res = null;
      try {
        if (api && typeof api.restoreSceneFromTrash === "function") {
          res = await api.restoreSceneFromTrash(sceneId, project.id);
        }
      } catch (err) {
        console.warn("Fallo al restaurar en servidor:", err);
      }

      // Regla: No limpies la copia local hasta confirmar la restauraciÃ³n persistida en servidor
      if (!res || !res.ok) {
        toast.error("No se pudo confirmar la restauraciÃ³n en el servidor. La copia de respaldo local fue preservada.");
        return;
      }

      // 2. Solo tras confirmar la restauraciÃ³n persistida en servidor, remover de la papelera local
      removeSceneFromTrash(userId, project.id, sceneId);
      setTrashScenes((prev) => prev.filter((it) => (it.scene?.id || it.id || it.scene_id) !== sceneId));

      const effective = res?.item || item;
      const sceneData = effective.scene || effective;
      const originalOrder = effective.original_order ?? effective.originalOrder ?? item.originalOrder;
      const nodes = effective.nodes || item.nodes || [];
      const edges = effective.edges || item.edges || [];

      dispatch({
        type: "SCENE_RESTORE",
        payload: {
          scene: sceneData,
          originalOrder,
          nodes,
          edges,
        },
      });

      if (undoNotice && (undoNotice.scene?.id === sceneId || undoNotice.id === sceneId)) {
        setUndoNotice(null);
      }
      refreshTrash();
    } finally {
      setRestoringIds((prev) => {
        const next = new Set(prev);
        next.delete(sceneId);
        return next;
      });
    }
  };

  const handleOpenPurgeModal = useCallback(async (target) => {
    setPurgeTarget(target);
    setDeleteFilesCheckbox(false);
    setPurgePreviewData(null);
    setPurgePreviewLoading(true);

    const targetSceneIds =
      target.mode === "single"
        ? [target.scene?.id || target.item?.scene?.id || target.item?.id]
        : (target.scenes || []).map((it) => it.scene?.id || it.id || it.scene_id).filter(Boolean);

    try {
      if (api && typeof api.purgeTrashPreview === "function") {
        const preview = await api.purgeTrashPreview({
          project_id: project.id,
          scene_ids: targetSceneIds,
        });
        setPurgePreviewData(preview);
      }
    } catch (err) {
      console.warn("Error consultando vista previa de borrado de archivos:", err);
    } finally {
      setPurgePreviewLoading(false);
    }
  }, [project.id]);

  const handleExecutePurge = async () => {
    if (!purgeTarget || isPurging) return;
    setIsPurging(true);

    const targetSceneIds =
      purgeTarget.mode === "single"
        ? [purgeTarget.scene?.id || purgeTarget.item?.scene?.id || purgeTarget.item?.id]
        : (purgeTarget.scenes || []).map((it) => it.scene?.id || it.id || it.scene_id).filter(Boolean);

    try {
      let res = null;
      if (api && typeof api.purgeTrashScenes === "function") {
        res = await api.purgeTrashScenes({
          project_id: project.id,
          scene_ids: targetSceneIds,
          delete_assets: Boolean(deleteFilesCheckbox),
        });
      }

      if (!res || !res.ok) {
        throw new Error(res?.detail || "No se pudo completar la eliminaciÃ³n definitiva en el servidor.");
      }

      // 1. Retirar inmediatamente de la papelera local y registrar tombstones
      permanentlyDeleteFromTrash(userId, project.id, targetSceneIds);

      // 2. Actualizar estado reactivo de la papelera
      setTrashScenes((prev) =>
        prev.filter((it) => !targetSceneIds.includes(it.scene?.id || it.id || it.scene_id))
      );

      // 3. Si habÃ­a un aviso de deshacer para una de estas escenas, retirarlo
      if (undoNotice && targetSceneIds.includes(undoNotice.scene?.id || undoNotice.id)) {
        setUndoNotice(null);
      }

      // 4. Si se eliminaron archivos fÃ­sicos, reflejar en el store
      if (res.deleted_asset_ids && res.deleted_asset_ids.length > 0) {
        dispatch({ type: "ASSETS_DELETE", payload: { ids: res.deleted_asset_ids } });
      }

      // 5. Informar al usuario con precisiÃ³n
      if (res.partial_errors && res.partial_errors.length > 0) {
        toast.warning(t.storyboard.purgePartialError || "Se eliminaron las escenas, pero algunos archivos no se pudieron borrar fÃ­sicamente. Se registrÃ³ para reintento.");
      } else {
        toast.success(
          purgeTarget.mode === "single"
            ? (t.storyboard.purgeSuccessSingle || "Escena eliminada definitivamente")
            : (t.storyboard.purgeSuccessBatch || "Papelera vaciada correctamente")
        );
      }

      setPurgeTarget(null);
      refreshTrash();
    } catch (err) {
      console.error("Fallo al eliminar definitivamente:", err);
      toast.error(err?.message || "Error al eliminar definitivamente");
    } finally {
      setIsPurging(false);
    }
  };

  const emptyBody = isOwner
    ? t.storyboard.emptyBody
    : (t.storyboard.emptyBodyGuest || "Crea tu primera toma para comenzar a estructurar tu proyecto.");
  const emptyActionLabel = t.storyboard.createFirstScene || "Crear primera toma";

  const decisions = state.continuityDecisions?.[project.id] || {};
  const projectChars = useMemo(() => {
    if (Array.isArray(projectCharacters) && projectCharacters.length > 0) {
      return projectCharacters;
    }
    return project?.id ? getProjectCharacters(state, project.id) : [];
  }, [projectCharacters, state, project?.id]);

  const projectRefs = useMemo(() => {
    if (Array.isArray(projectReferences) && projectReferences.length > 0) {
      return projectReferences;
    }
    return project?.id ? getProjectReferences(state, project.id) : [];
  }, [projectReferences, state, project?.id]);

  const authorizedAssets = useMemo(
    () => getAuthorizedProjectAssets(state, project.id),
    [state, project.id]
  );

  const allGalleryAssets = useMemo(
    () => getAllUserGalleryAssets(state, (a) => a.type === "image" || a.type === "video"),
    [state]
  );

  const [assignModalScene, setAssignModalScene] = useState(null);
  const [pendingReplace, setPendingReplace] = useState(null);

  // Estado de subida desde el dispositivo y persistencia
  const [uploadState, setUploadState] = useState({
    isUploading: false,
    progress: 0,
    targetSceneId: null,
    file: null,
    uploadedAsset: null,
    error: null,
    step: "idle",
  });
  const uploadFileInputRef = useRef(null);
  const targetSceneIdRef = useRef(null);

  const handleStartUpload = (targetScene) => {
    const sceneToUse = targetScene || assignModalScene || selected;
    if (!sceneToUse) return;
    targetSceneIdRef.current = sceneToUse.id;
    if (uploadFileInputRef.current) {
      uploadFileInputRef.current.value = "";
      uploadFileInputRef.current.click();
    }
  };

  const handleFileChosen = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const targetSceneId = targetSceneIdRef.current;
    const targetScene = scenes.find((s) => s.id === targetSceneId) || assignModalScene || selected;
    if (!targetScene) return;

    const allowedMime = ["image/png", "image/jpeg", "image/webp"];
    if (!allowedMime.includes(file.type)) {
      setUploadState({
        isUploading: false,
        progress: 0,
        targetSceneId: targetScene.id,
        file,
        uploadedAsset: null,
        error: t.storyboard?.unsupportedImageFormat || "Formato no compatible. Usa PNG, JPEG o WebP.",
        step: "error_upload",
      });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadState({
        isUploading: false,
        progress: 0,
        targetSceneId: targetScene.id,
        file,
        uploadedAsset: null,
        error: t.storyboard?.fileTooLarge10MB || "La imagen supera el lÃ­mite de 10 MB.",
        step: "error_upload",
      });
      return;
    }

    startUploadProcess(file, targetScene.id);
  };

  const startUploadProcess = async (file, targetSceneId) => {
    setUploadState({
      isUploading: true,
      progress: 0,
      targetSceneId,
      file,
      uploadedAsset: null,
      error: null,
      step: "uploading",
    });

    try {
      const uploadFn = api.uploadAssetWithProgress || uploadAssetWithProgress;
      const uploaded = await uploadFn(file, {
        filename: file.name,
        onProgress: (pct) => {
          setUploadState((prev) => ({ ...prev, progress: pct }));
        },
      });

      const newAsset = Asset({
        id: uploaded.id,
        uploadId: uploaded.id,
        projectId: project.id,
        sceneId: null,
        type: "image",
        url: uploaded.url,
        label: file.name,
        filename: file.name,
        width: uploaded.width,
        height: uploaded.height,
        mime: uploaded.mime,
        size: uploaded.size,
        source: "upload",
        isDemo: false,
      });

      dispatch({ type: "ASSET_ADD", payload: newAsset });

      setUploadState({
        isUploading: false,
        progress: 100,
        targetSceneId,
        file,
        uploadedAsset: newAsset,
        error: null,
        step: "assigning",
      });

      attemptAssignMediaToScene(newAsset, targetSceneId);
    } catch (err) {
      setUploadState({
        isUploading: false,
        progress: 0,
        targetSceneId,
        file,
        uploadedAsset: null,
        error: err.message || t.storyboard?.uploadError || "Error al subir imagen",
        step: "error_upload",
      });
    }
  };

  const attemptAssignMediaToScene = (asset, targetSceneId) => {
    const freshScene = (state.scenes || []).find((s) => s.id === targetSceneId);
    if (!freshScene) {
      setUploadState((prev) => ({ ...prev, isUploading: false, step: "idle" }));
      return;
    }

    try {
      if (freshScene.selectedAssetId && freshScene.selectedAssetId !== asset.id) {
        setPendingReplace({ scene: freshScene, newAsset: asset });
        setAssignModalScene(null);
      } else {
        executeAssignMedia(freshScene, asset);
        setAssignModalScene(null);
        setUploadState((prev) => ({ ...prev, isUploading: false, step: "idle" }));
      }
    } catch (err) {
      setUploadState((prev) => ({
        ...prev,
        error: t.storyboard?.assignErrorPreserved || "No se pudo asignar a la escena, pero el archivo se conservÃ³ en tu galerÃ­a.",
        step: "error_assign",
      }));
    }
  };

  const handleRetryUpload = () => {
    if (uploadState.file && uploadState.targetSceneId) {
      startUploadProcess(uploadState.file, uploadState.targetSceneId);
    }
  };

  const handleRetryAssign = () => {
    if (uploadState.uploadedAsset && uploadState.targetSceneId) {
      attemptAssignMediaToScene(uploadState.uploadedAsset, uploadState.targetSceneId);
    }
  };

  const handleAssignMedia = (targetScene) => {
    setAssignModalScene(targetScene);
  };

  const handlePickAssetForScene = (targetScene, asset) => {
    if (!targetScene || !asset) return;
    if (targetScene.selectedAssetId && targetScene.selectedAssetId !== asset.id) {
      setPendingReplace({ scene: targetScene, newAsset: asset });
    } else {
      executeAssignMedia(targetScene, asset);
    }
  };

  const executeAssignMedia = (targetScene, asset) => {
    dispatch({
      type: "SCENE_SELECT_ASSET",
      payload: { sceneId: targetScene.id, assetId: asset.id },
    });
    dispatch({
      type: "SELECT",
      payload: {
        projectId: project.id,
        patch: { sceneId: targetScene.id, assetId: asset.id },
      },
    });
  };

  const observations = useMemo(
    () => analyzeProjectContinuity({ scenes, characters: projectChars }),
    [scenes, projectChars]
  );

  const lastSelectedIdRef = useRef(selection?.sceneId);
  useEffect(() => {
    if (selection?.sceneId && selection.sceneId !== lastSelectedIdRef.current) {
      lastSelectedIdRef.current = selection.sceneId;
      setDetailExpanded(true);
    }
  }, [selection?.sceneId]);

  const selected = scenes.find((s) => s.id === selection.sceneId);
  const select = (s) => {
    setDetailExpanded(true);
    dispatch({ type: "SELECT", payload: { projectId: project.id, patch: { sceneId: s.id, assetId: s.selectedAssetId } } });
  };
  const addScene = () => dispatch({ type: "SCENE_CREATE", payload: { projectId: project.id, title: t.storyboard.sceneN(scenes.length + 1) } });

  return (
    <div data-testid="storyboard-view" className="flex min-h-0 min-w-0 flex-1 flex-col lg:flex-row overflow-hidden">
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-4 md:p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="text-sm text-[#a3a3a3]">{t.projects.scenesCount(scenes.length)}</div>
          <div className="flex items-center gap-2">
            <button
              data-testid="storyboard-trash-btn"
              onClick={() => setTrashOpen(true)}
              className="v-btn-ghost v-btn-sm flex items-center gap-1.5 text-xs text-[#a3a3a3] hover:text-[#f4f1ec]"
              title={t.storyboard.trashTitle || "Papelera del proyecto"}
            >
              <Trash2 size={13} />
              <span>
                {t.storyboard.trash || "Papelera"}
                {trashScenes.length > 0 ? ` (${trashScenes.length})` : ""}
              </span>
            </button>
            <button data-testid="storyboard-add-scene" className="v-btn-primary v-btn-sm flex items-center gap-1.5" onClick={addScene}>
              <Plus size={14} />
              <span>{t.storyboard.addScene}</span>
            </button>
          </div>
        </div>

        {/* â”€â”€ Banner de Deshacer eliminaciÃ³n de escena â”€â”€ */}
        {undoNotice && (
          <div
            data-testid="storyboard-undo-banner"
            className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-[#f5b544]/50 bg-[#1e1a12] px-4 py-2.5 text-xs md:text-sm text-[#f4f1ec] shadow-lg"
          >
            <div className="flex items-center gap-2 truncate">
              <span className="h-2 w-2 rounded-full bg-[#f5b544] shrink-0" />
              <span className="truncate">
                {t.storyboard.sceneDeleted || "Escena eliminada"}
                {undoNotice.scene?.title ? ` Â«${undoNotice.scene.title}Â»` : ""}
              </span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                data-testid="storyboard-undo-btn"
                onClick={handleUndoDelete}
                className="flex items-center gap-1.5 font-semibold text-[#f5b544] hover:underline"
              >
                <Undo2 size={13} />
                <span>{t.storyboard.undo || "Deshacer"}</span>
              </button>
              <button
                type="button"
                data-testid="storyboard-undo-dismiss"
                onClick={() => setUndoNotice(null)}
                className="text-[#888] hover:text-[#f4f1ec] text-xs px-1"
                aria-label="Cerrar aviso"
              >
                âœ•
              </button>
            </div>
          </div>
        )}
        {scenes.length === 0 ? (
          <div className="flex h-[60vh] min-h-[340px] w-full items-center justify-center p-4">
            <EmptyState
              title={t.storyboard.empty}
              body={emptyBody}
              testId="storyboard-empty"
              action={
                <button
                  className="v-btn-primary v-btn-sm flex items-center gap-1.5"
                  data-testid="storyboard-empty-add"
                  onClick={addScene}
                >
                  <Plus size={14} />
                  <span>{emptyActionLabel}</span>
                </button>
              }
            />
          </div>
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
            {scenes.map((s, i) => (
              <SceneCard
                key={s.id}
                scene={s}
                index={i}
                total={scenes.length}
                selected={s.id === selection.sceneId}
                cover={assetById[s.selectedAssetId]}
                onSelect={() => select(s)}
                onMove={(dir) => dispatch({ type: "SCENE_MOVE", payload: { id: s.id, dir } })}
                onDuplicate={() => dispatch({ type: "SCENE_DUPLICATE", payload: { id: s.id } })}
                onDelete={() => setToDelete(s)}
                onAssignMedia={handleAssignMedia}
                onUploadMedia={handleStartUpload}
                assetById={assetById}
                project={project}
                observations={observations}
                decisions={decisions}
              />
            ))}
          </div>
        )}
      </div>
      {selected && (
        <div
          data-testid="scene-detail-panel"
          className={`flex shrink-0 flex-col border-t border-[#303030] bg-[#161616] lg:border-l lg:border-t-0 transition-all duration-150 ${detailExpanded ? "lg:w-[400px] xl:w-[420px]" : "lg:w-auto"
            }`}
        >
          <button
            type="button"
            data-testid="scene-detail-toggle"
            onClick={() => setDetailExpanded((e) => !e)}
            aria-expanded={detailExpanded}
            aria-pressed={detailExpanded}
            aria-label={detailExpanded ? t.storyboard.hideDetail : t.storyboard.showDetail}
            title={detailExpanded ? t.storyboard.hideDetail : t.storyboard.showDetail}
            className={`flex shrink-0 items-center justify-between gap-3 px-4 py-3 text-left transition-colors border-b border-[#282828] select-none ${detailExpanded
                ? "bg-[#1c1c1c] text-[#f4f1ec] border-l-2 border-l-[#f5b544]"
                : "bg-[#141414] text-[#a3a3a3] hover:text-[#f4f1ec] hover:bg-[#1a1a1a]"
              }`}
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className={`v-eyebrow ${detailExpanded ? "text-[#f5b544]" : "text-[#888]"}`}>
                {t.storyboard.detail}
              </span>
              <span className={`truncate text-xs ${detailExpanded ? "text-[#a3a3a3]" : "text-[#737373]"}`}>
                Â· {selected.title || t.common.untitled}
              </span>
            </span>
            {detailExpanded ? (
              <ChevronUp size={16} className="shrink-0 text-[#f5b544]" />
            ) : (
              <ChevronDown size={16} className="shrink-0 text-[#a3a3a3]" />
            )}
          </button>
          {detailExpanded && (
            <div className="max-h-[50vh] overflow-y-auto lg:max-h-none lg:flex-1 lg:overflow-y-auto">
              <SceneDetail
                key={selected.id}
                scene={selected}
                project={project}
                projectId={project.id}
                scenes={scenes}
                observations={observations}
                decisions={decisions}
                characters={projectChars}
                references={projectRefs}
                onAssignMedia={handleAssignMedia}
                onUploadMedia={handleStartUpload}
              />
            </div>
          )}
        </div>
      )}
      <input
        type="file"
        ref={uploadFileInputRef}
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleFileChosen}
        data-testid="storyboard-direct-file-input"
      />
      {assignModalScene && (
        <AssetPicker
          open={Boolean(assignModalScene)}
          onOpenChange={(o) => !o && setAssignModalScene(null)}
          title={
            typeof t.storyboard.chooseFromLibraryTitle === "function"
              ? t.storyboard.chooseFromLibraryTitle(
                assignModalScene.title || t.storyboard.sceneN(scenes.indexOf(assignModalScene) + 1)
              )
              : t.storyboard.chooseFromLibrary
          }
          allowScopeToggle={true}
          projectAssets={authorizedAssets}
          allAssets={allGalleryAssets}
          defaultScope="project"
          assets={authorizedAssets}
          filter={(a) => a.type === "image" || a.type === "video"}
          confirmLabel={
            typeof t.storyboard.assignToSceneNamed === "function"
              ? t.storyboard.assignToSceneNamed(
                assignModalScene.title || t.storyboard.sceneN(scenes.indexOf(assignModalScene) + 1)
              )
              : t.storyboard.assignToThisScene
          }
          onPick={(asset) => {
            handlePickAssetForScene(assignModalScene, asset);
          }}
          onUploadClick={(file) => {
            if (file) {
              startUploadProcess(file, assignModalScene.id);
            } else {
              handleStartUpload(assignModalScene);
            }
          }}
          uploadState={uploadState}
          onRetryUpload={handleRetryUpload}
          onRetryAssign={handleRetryAssign}
        />
      )}
      <ConfirmDialog
        open={Boolean(pendingReplace)}
        onOpenChange={(o) => !o && setPendingReplace(null)}
        title={
          typeof t.storyboard.replaceMediaTitle === "function"
            ? t.storyboard.replaceMediaTitle(
              pendingReplace?.scene?.title ||
              t.storyboard.sceneN(scenes.indexOf(pendingReplace?.scene) + 1)
            )
            : t.storyboard.replaceMediaTitle
        }
        body={t.storyboard.replaceMediaBody}
        confirmLabel={t.storyboard.replaceMediaConfirm}
        destructive={false}
        onConfirm={() => {
          if (pendingReplace) {
            executeAssignMedia(pendingReplace.scene, pendingReplace.newAsset);
            setPendingReplace(null);
          }
        }}
      />
      <ConfirmDialog
        open={Boolean(toDelete)}
        onOpenChange={(o) => !o && setToDelete(null)}
        title={toDelete?.title || t.common.untitled}
        body={t.storyboard.deleteConfirm}
        onConfirm={() => handleDeleteScene(toDelete)}
      />

      {/* â”€â”€ Modal de Papelera de Escenas con recuperaciÃ³n persistente â”€â”€ */}
      <Dialog open={trashOpen} onOpenChange={setTrashOpen}>
        <DialogContent
          data-testid="storyboard-trash-modal"
          className="max-w-xl border-[#303030] bg-[#161616] p-6 text-[#f4f1ec]"
        >
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-6">
              <div>
                <DialogTitle className="flex items-center gap-2 text-base font-semibold">
                  <Trash2 size={16} className="text-[#f5b544]" />
                  <span>{t.storyboard.trashTitle || "Papelera del proyecto"}</span>
                </DialogTitle>
                <DialogDescription className="text-xs text-[#a3a3a3]">
                  {t.storyboard.trashSubtitle || "Las escenas eliminadas de este proyecto se conservan aquÃ­ para poder recuperarlas."}
                </DialogDescription>
              </div>
              {trashScenes.length > 0 && (
                <button
                  type="button"
                  data-testid="trash-empty-all-btn"
                  onClick={() => handleOpenPurgeModal({ mode: "batch", scenes: trashScenes })}
                  disabled={isPurging}
                  className="v-btn-ghost v-btn-sm text-red-400 hover:text-red-300 hover:bg-red-950/30 text-xs shrink-0 flex items-center gap-1.5"
                >
                  <Trash2 size={13} />
                  <span>{t.storyboard.emptyTrash || "Vaciar papelera"}</span>
                </button>
              )}
            </div>
          </DialogHeader>

          {trashSyncError && (
            <div
              data-testid="trash-sync-error-banner"
              className="mt-3 flex items-center justify-between gap-2 rounded border border-amber-600/40 bg-amber-950/30 p-2.5 text-xs text-amber-200"
            >
              <div className="flex items-center gap-2">
                <AlertCircle size={14} className="shrink-0 text-amber-400" />
                <span>{t.storyboard.trashSyncError || "No se pudo sincronizar la papelera con el servidor."}</span>
              </div>
              <button
                type="button"
                data-testid="trash-sync-retry-btn"
                onClick={refreshTrash}
                disabled={isTrashLoading}
                className="rounded bg-amber-600/20 px-2 py-1 text-xs font-semibold text-[#f5b544] hover:bg-amber-600/30 transition-colors"
              >
                {t.storyboard.trashSyncRetry || "Reintentar"}
              </button>
            </div>
          )}

          <div className="mt-4 max-h-80 overflow-y-auto space-y-2.5 pr-1">
            {trashScenes.length === 0 ? (
              <div data-testid="trash-empty-notice" className="py-8 text-center text-xs text-[#777]">
                {t.storyboard.trashEmpty || "La papelera estÃ¡ vacÃ­a"}
              </div>
            ) : (
              trashScenes.map((item) => {
                const sc = item.scene;
                const cover = assetById[sc.selectedAssetId];
                const isRestoring = restoringIds.has(sc.id);
                const dateStr = item.deletedAt ? new Date(item.deletedAt).toLocaleString() : "";

                return (
                  <div
                    key={sc.id}
                    data-testid={`trash-item-${sc.id}`}
                    className="flex items-center justify-between gap-3 rounded-lg border border-[#2b2b2b] bg-[#1c1c1c] p-3 transition-colors hover:border-[#3d3d3d]"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {cover ? (
                        <MediaFrame asset={cover} className="h-12 w-20 shrink-0 rounded bg-[#101010]" fit="contain" />
                      ) : (
                        <div className="h-12 w-20 shrink-0 rounded border border-[#333] bg-[#121212] flex items-center justify-center text-[10px] text-[#555]">
                          Sin medio
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="truncate text-xs font-medium text-[#ededed]">
                          {sc.title || t.common.untitled}
                        </div>
                        {sc.prompt && (
                          <div className="line-clamp-1 text-[11px] text-[#888]">{sc.prompt}</div>
                        )}
                        <div className="mt-0.5 flex items-center gap-2 text-[10px] text-[#666]">
                          {sc.duration != null && <span>{sc.duration}s</span>}
                          {dateStr && (
                            <span>
                              {typeof t.storyboard.deletedAt === "function"
                                ? t.storyboard.deletedAt(dateStr)
                                : `Eliminada: ${dateStr}`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        data-testid={`trash-restore-${sc.id}`}
                        onClick={() => handleRestoreFromTrash(item)}
                        disabled={isRestoring || isPurging}
                        className="v-btn-primary v-btn-sm flex items-center gap-1.5 text-xs disabled:opacity-50"
                      >
                        <RotateCcw size={12} />
                        <span>{isRestoring ? "Restaurandoâ€¦" : (t.storyboard.restore || "Restaurar")}</span>
                      </button>

                      <button
                        type="button"
                        data-testid={`trash-purge-${sc.id}`}
                        onClick={() => handleOpenPurgeModal({ mode: "single", scene: sc, item })}
                        disabled={isRestoring || isPurging}
                        className="v-btn-ghost v-btn-sm text-red-400 hover:text-red-300 hover:bg-red-950/30 text-xs flex items-center gap-1.5 disabled:opacity-50"
                        title={t.storyboard.permanentDelete || "Eliminar definitivamente"}
                      >
                        <Trash2 size={12} />
                        <span>{t.storyboard.permanentDelete || "Eliminar definitivamente"}</span>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="mt-4 flex justify-end">
            <button
              data-testid="trash-modal-close"
              onClick={() => setTrashOpen(false)}
              className="v-btn-ghost v-btn-sm"
            >
              {t.common.close || "Cerrar"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* â”€â”€ Modal de ConfirmaciÃ³n de EliminaciÃ³n Definitiva y Vaciado â”€â”€ */}
      <Dialog
        open={Boolean(purgeTarget)}
        onOpenChange={(o) => {
          if (!o && !isPurging) {
            setPurgeTarget(null);
          }
        }}
      >
        <DialogContent
          data-testid="trash-purge-confirm-dialog"
          className="max-w-md border-red-900/40 bg-[#161616] p-6 text-[#f4f1ec]"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-semibold text-red-400">
              <Trash2 size={18} className="text-red-400" />
              <span>
                {purgeTarget?.mode === "single"
                  ? (typeof t.storyboard.purgeConfirmTitleSingle === "function"
                    ? t.storyboard.purgeConfirmTitleSingle(purgeTarget?.scene?.title || t.common.untitled)
                    : `Â¿Eliminar definitivamente Â«${purgeTarget?.scene?.title || t.common.untitled}Â»?`)
                  : (typeof t.storyboard.purgeConfirmTitleBatch === "function"
                    ? t.storyboard.purgeConfirmTitleBatch(purgeTarget?.scenes?.length || trashScenes.length)
                    : `Â¿Vaciar la papelera (${purgeTarget?.scenes?.length || trashScenes.length} escenas)?`)}
              </span>
            </DialogTitle>
            <DialogDescription className="text-xs text-[#a3a3a3] mt-1.5 leading-relaxed">
              {t.storyboard.purgeConfirmWarning || "Esta acciÃ³n es irreversible. Las escenas afectadas ya no podrÃ¡n recuperarse."}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4 text-xs">
            {/* Aviso de opciÃ³n predeterminada */}
            <div className="rounded-md border border-[#2b2b2b] bg-[#1a1a1a] p-3 text-[#b3b3b3] leading-relaxed">
              {t.storyboard.purgeDefaultNotice ||
                "Por defecto solo se eliminarÃ¡n las escenas de la papelera. Sus imÃ¡genes y vÃ­deos se conservarÃ¡n en la GalerÃ­a, y sus personajes, objetos y referencias en la biblioteca."}
            </div>

            {/* Casilla desmarcada por defecto para eliminar archivos exclusivos */}
            <div className="rounded-md border border-[#333] bg-[#141414] p-3.5 space-y-2">
              <label className="flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="trash-purge-delete-files-checkbox"
                  checked={deleteFilesCheckbox}
                  onChange={(e) => setDeleteFilesCheckbox(e.target.checked)}
                  disabled={isPurging}
                  className="mt-0.5 h-4 w-4 rounded border-gray-600 text-[#f5b544] focus:ring-0 focus:ring-offset-0 bg-[#222]"
                />
                <span className="font-medium text-[#ededed] leading-snug">
                  {t.storyboard.deleteFilesCheckbox || "Eliminar tambiÃ©n los archivos utilizados Ãºnicamente por estas escenas"}
                </span>
              </label>
              <p className="text-[11px] text-[#777] pl-6 leading-relaxed">
                {t.storyboard.deleteFilesNote || "Solo se retirarÃ¡n archivos directos sin otro uso. Los personajes, objetos y fichas de referencias nunca se eliminarÃ¡n."}
              </p>

              {/* MÃ©tricas calculadas por el backend */}
              <div className="mt-2.5 pt-2.5 border-t border-[#262626] pl-6 text-[11px] space-y-1 text-[#aaa]">
                {purgePreviewLoading ? (
                  <div data-testid="trash-purge-preview-loading" className="text-[#888] italic">
                    {t.storyboard.calculatingUsage || "Calculando uso de archivosâ€¦"}
                  </div>
                ) : purgePreviewData ? (
                  <div data-testid="trash-purge-preview-stats" className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span>
                        {typeof t.storyboard.deletableFilesCount === "function"
                          ? t.storyboard.deletableFilesCount(purgePreviewData.deletable_count ?? 0)
                          : `${purgePreviewData.deletable_count ?? 0} archivo(s) exclusivo(s) eliminable(s)`}
                      </span>
                      <span className="font-mono text-[#f4f1ec]">
                        {purgePreviewData.space_available && purgePreviewData.space_to_free_formatted
                          ? (typeof t.storyboard.spaceFreedEstimate === "function"
                            ? t.storyboard.spaceFreedEstimate(purgePreviewData.space_to_free_formatted)
                            : `Espacio a liberar: ${purgePreviewData.space_to_free_formatted}`)
                          : (t.storyboard.spaceUnavailable || "Espacio no disponible")}
                      </span>
                    </div>
                    {(purgePreviewData.protected_count ?? 0) > 0 && (
                      <div className="text-[#777]">
                        {typeof t.storyboard.protectedFilesCount === "function"
                          ? t.storyboard.protectedFilesCount(purgePreviewData.protected_count)
                          : `${purgePreviewData.protected_count} archivo(s) protegido(s) por otros usos`}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              data-testid="trash-purge-cancel"
              onClick={() => setPurgeTarget(null)}
              disabled={isPurging}
              className="v-btn-ghost v-btn-sm"
            >
              {t.common.cancel || "Cancelar"}
            </button>
            <button
              type="button"
              data-testid="trash-purge-confirm"
              onClick={handleExecutePurge}
              disabled={isPurging || purgePreviewLoading}
              className="v-btn-primary v-btn-sm bg-red-600 hover:bg-red-700 text-white border-red-500 disabled:opacity-50"
            >
              {isPurging
                ? (t.storyboard.purging || "Eliminandoâ€¦")
                : (t.storyboard.permanentDelete || "Eliminar definitivamente")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoryboardView;
